import React, { useState, useEffect } from 'react';
import SearchableSelect from './SearchableSelect';
import Swal from 'sweetalert2';
import './VehicleRegistrationReport.css';

const STATUS_OPTIONS = [
  { key: 'inUseNotTransferred', label: 'Xe đang sử dụng chưa sang tên' },
  { key: 'soldNotRevoked', label: 'Xe đã bán chưa thu hồi' },
  { key: 'transferRequested', label: 'Yêu cầu sang tên' },
  { key: 'transferCompleted', label: 'Đã hoàn thành sang tên' },
  { key: 'broken', label: 'Xe hỏng' },
  { key: 'lost', label: 'Xe mất' },
  { key: 'systemUpdated', label: 'Cập nhật hệ thống' },
  { key: 'transferredToInspection', label: 'Chuyển TTKS/XLVP/ĐTGQTN' },
  { key: 'receivedFromInspection', label: 'DS nhận từ TTKS/XLVP/ĐTGQTN' }
];

const VehicleRegistrationReportForm = ({ initialData, onSave, onCancel, user, units = [] }) => {
  const [formData, setFormData] = useState({
    licensePlate: '',
    unitId: user?.unitId || '',
    notes: '',
    inUseNotTransferred: false,
    soldNotRevoked: false,
    transferRequested: false,
    transferCompleted: false,
    broken: false,
    lost: false,
    systemUpdated: false,
    transferredToInspection: false,
    receivedFromInspection: false
  });

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData({
        licensePlate: initialData.licensePlate || '',
        unitId: initialData.unitId || user?.unitId || '',
        notes: initialData.notes || '',
        inUseNotTransferred: Boolean(initialData.inUseNotTransferred),
        soldNotRevoked: Boolean(initialData.soldNotRevoked),
        transferRequested: Boolean(initialData.transferRequested),
        transferCompleted: Boolean(initialData.transferCompleted),
        broken: Boolean(initialData.broken),
        lost: Boolean(initialData.lost),
        systemUpdated: Boolean(initialData.systemUpdated),
        transferredToInspection: Boolean(initialData.transferredToInspection),
        receivedFromInspection: Boolean(initialData.receivedFromInspection)
      });
    } else {
      setFormData(prev => ({
        ...prev,
        unitId: user?.unitId || ''
      }));
    }
  }, [initialData, user]);

  const handleCheckboxChange = (key) => {
    setFormData(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const getSelectedCount = () => {
    return STATUS_OPTIONS.filter(opt => formData[opt.key]).length;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.licensePlate || !formData.licensePlate.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Thiếu thông tin',
        text: 'Vui lòng nhập biển số xe rà soát'
      });
      return;
    }

    if (getSelectedCount() === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Chưa chọn trạng thái',
        text: 'Bắt buộc phải tích chọn tối thiểu 1 thông tin trạng thái!'
      });
      return;
    }

    try {
      setSubmitting(true);
      await onSave(formData);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Ngày hiển thị (hôm nay hoặc ngày của bản ghi)
  const todayFormatted = new Date().toLocaleDateString('vi-VN');
  const userUnitName = user?.unit?.name || (units.find(u => u.id === user?.unitId)?.name) || 'Chưa xác định';

  return (
    <div className="vrr-modal-overlay" onClick={onCancel}>
      <div className="vrr-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="vrr-modal-header">
          <h3>{initialData ? '✏️ Chỉnh sửa Rà soát sở hữu xe' : '➕ Thêm mới Rà soát sở hữu xe'}</h3>
          <button className="vrr-modal-close" onClick={onCancel}>&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="vrr-modal-body">
          <div className="vrr-form-row">
            {/* Đơn vị */}
            <div className="vrr-form-group">
              <label>Đơn vị thực hiện:</label>
              {(user?.role === 'admin' || user?.role === 'supervisor') ? (
                <SearchableSelect
                  options={[
                    ...units.map(unit => ({ value: unit.id, label: unit.name }))
                  ]}
                  value={formData.unitId}
                  onChange={(val) => setFormData(prev => ({ ...prev, unitId: val }))}
                  placeholder="-- Chọn đơn vị --"
                />
              ) : (
                <input
                  type="text"
                  value={userUnitName}
                  disabled
                  readOnly
                  className="vrr-readonly-input"
                />
              )}
            </div>

            {/* Ngày rà soát */}
            <div className="vrr-form-group">
              <label>Ngày rà soát (Tự động ngày hiện tại):</label>
              <input
                type="text"
                value={todayFormatted}
                disabled
                readOnly
                className="vrr-readonly-input"
                title="Ngày được hệ thống tự động gán là ngày hiện tại"
              />
            </div>
          </div>

          {/* Biển số xe */}
          <div className="vrr-form-group">
            <label>Số chủ xe rà soát / Biển số xe (*):</label>
            <input
              type="text"
              placeholder="Ví dụ: 86A-12345 hoặc 86C-99999"
              value={formData.licensePlate}
              onChange={(e) => setFormData(prev => ({ ...prev, licensePlate: e.target.value.toUpperCase() }))}
              required
              autoFocus
            />
          </div>

          {/* Lưới Checkbox 9 trạng thái */}
          <div className="vrr-checkbox-section">
            <div className="vrr-checkbox-section-title">
              <span>📋 Tình trạng phương tiện rà soát (Đã chọn: {getSelectedCount()}/9)</span>
              <span className="vrr-checkbox-hint">* Tối thiểu 1 lựa chọn</span>
            </div>

            <div className="vrr-checkbox-grid">
              {STATUS_OPTIONS.map((option) => {
                const isChecked = Boolean(formData[option.key]);
                return (
                  <label
                    key={option.key}
                    className={`vrr-checkbox-card ${isChecked ? 'checked' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => handleCheckboxChange(option.key)}
                    />
                    <span>{option.label}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Ghi chú */}
          <div className="vrr-form-group">
            <label>Ghi chú bổ sung:</label>
            <textarea
              rows="3"
              placeholder="Nhập thông tin ghi chú khác (nếu có)..."
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            />
          </div>

          {/* Modal actions */}
          <div className="vrr-modal-actions">
            <button type="button" className="vrr-btn-cancel" onClick={onCancel}>
              Hủy
            </button>
            <button type="submit" className="vrr-btn-save" disabled={submitting}>
              {submitting ? '⏳ Đang lưu...' : (initialData ? '💾 Lưu thay đổi' : '➕ Thêm mới')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VehicleRegistrationReportForm;
